import { leafName } from "@/dnd";
import type { DraggableData } from "@/dnd/ids";
import {
  buildFilesRailDragPayload,
  resolveFilesRailDragEntries,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/buildFilesRailDragPayload";
import {
  FilesRailFileRow,
  type FilesRailFileMenuActions,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/files/FilesRailFileRow";
import {
  railSelectionKey,
  useRailSelectionOptional,
  type RailSelectionSource,
} from "@/layouts/WorkspaceLayout/components/WorkspaceLeftRail/rail/RailSelectionContext";
import type { FileRecord } from "@cohesive/api-client";
import { useDraggable } from "@dnd-kit/core";
import { useMemo } from "react";

/** Draggable file row — must render inside `CanvasDndProvider`. */
export function FilesRailDraggableFileRow({
  record,
  workspaceId,
  dragNamespace,
  active,
  section = "canonical",
  onOpen,
  menuActions,
  sharedSource,
  withChevronSpacer,
}: {
  record: FileRecord;
  workspaceId: string;
  dragNamespace: string;
  active?: boolean;
  section?: RailSelectionSource;
  onOpen: () => void;
  menuActions?: FilesRailFileMenuActions;
  /** Mark the drag as a shared-with-me file → copy (not move) into a workspace. */
  sharedSource?: boolean;
  /** Align with expandable folder siblings at the same tree depth. */
  withChevronSpacer?: boolean;
}) {
  const selection = useRailSelectionOptional();
  const rowKey = railSelectionKey(section, "file", record.id);

  const draggableData: DraggableData = useMemo(() => {
    const selectedResources = selection?.getSelectedResources() ?? [];
    const entries = resolveFilesRailDragEntries({
      selectedResources,
      draggedIsSelected: !!selection?.isSelected(rowKey),
      dragged: { record, sharedSource },
    });
    return {
      kind: "files" as const,
      payloads: entries.map(({ record: r, sharedSource: fileShared }) =>
        buildFilesRailDragPayload(r, r.workspace_id ?? workspaceId, {
          sharedSource: fileShared,
        }),
      ),
      primaryName:
        entries.length === 1
          ? leafName(entries[0]!.record.path)
          : `${entries.length} files`,
    };
  }, [record, workspaceId, sharedSource, selection, rowKey]);

  const draggable = useDraggable({
    id: `file-row:${dragNamespace}:${record.id}`,
    data: draggableData,
  });

  return (
    <FilesRailFileRow
      record={record}
      active={active}
      section={section}
      onOpen={onOpen}
      menuActions={menuActions}
      sharedSource={sharedSource}
      withChevronSpacer={withChevronSpacer}
      dragRef={draggable.setNodeRef}
      dragProps={{
        ...draggable.attributes,
        ...draggable.listeners,
      }}
      isDragging={draggable.isDragging}
    />
  );
}
